import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../zodios/client";
import type { BulkAdScheduledActionsUpdate } from "../../../zodios/apiDefinitions/api/ads";
import { errorToast, successToast } from "../../../utils/toasts";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { getScheduledActionConflictRange, isScheduledActionOverlap } from "../utils/scheduledActionOverlap";

export function useBulkAdScheduledActionsUpdate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);
  const dateFormatter = useDateFormatter("medium");
  return useMutation({
    // The overlap refusal below is a rejection this flow explains, not a bug to report.
    meta: { expectedError: isScheduledActionOverlap },
    mutationFn: async (vars: { data: BulkAdScheduledActionsUpdate; isLast: boolean }) => {
      return await apiClient.updateAdScheduledActions(vars.data);
    },
    onSuccess: (data, vars) => {
      if (vars.isLast) {
        const count = vars.data.gvadvertisements_set.length;
        successToast({ message: t(($) => $.advertising["Media Plans Updated"], { count }) });
        // TODO: shouldn't invalidate _all_ queries
        void queryClient.invalidateQueries();
      }
    },
    onError: (error, vars) => {
      const conflictingRange = getScheduledActionConflictRange(error, dateFormatter);

      if (conflictingRange) {
        errorToast({
          message: t(
            ($) =>
              $.advertising[
                "Failed to create scheduled update. The selected dates overlap a scheduled action already running {dateRange}."
              ],
            { dateRange: conflictingRange },
          ),
          error,
        });
        return;
      }

      if (vars.isLast) {
        const count = vars.data.gvadvertisements_set.length;
        errorToast({ message: t(($) => $.advertising["Failed to update Media Plans"], { count }) });
      }
    },
  });
}
